export const VO_HIGHLIGHT_SEEN_KEY = "xpro.voHighlights.seen";
export const VO_HIGHLIGHT_REACTIONS = ["👏", "🎉", "👋", "🏆"];

export function normalizeHighlightReactions(rows) {
  const out = [];
  const seen = new Set();
  for (const row of Array.isArray(rows) ? rows : []) {
    const emoji = String(row?.emoji || "").trim();
    const fromUserId = Number(row?.fromUserId || row?.from_user_id);
    if (!VO_HIGHLIGHT_REACTIONS.includes(emoji) || !fromUserId) continue;
    const key = `${fromUserId}:${emoji}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      emoji,
      fromUserId,
      fromName: String(row.fromName || row.from_name || "").trim(),
      fromPhoto: row.fromPhoto || row.from_photo || null,
    });
  }
  return out;
}

export function appendHighlightReaction(item, reaction) {
  if (!item) return item;
  const next = normalizeHighlightReactions([...(item.reactions || []), reaction]);
  if (next.length === (item.reactions || []).length) return item;
  return { ...item, reactions: next };
}

export function groupHighlightReactions(reactions = []) {
  const byEmoji = new Map();
  for (const row of normalizeHighlightReactions(reactions)) {
    const list = byEmoji.get(row.emoji) || [];
    list.push(row);
    byEmoji.set(row.emoji, list);
  }
  return VO_HIGHLIGHT_REACTIONS.filter((emoji) => byEmoji.has(emoji)).map((emoji) => ({
    emoji,
    people: byEmoji.get(emoji),
    count: byEmoji.get(emoji).length,
  }));
}

export function unwrapHighlights(payload) {
  const raw = payload?.data?.data || payload?.data || payload;
  const items = Array.isArray(raw?.items) ? raw.items : Array.isArray(raw) ? raw : [];
  return items
    .map((row) => ({
      id: row.id ?? `${row.kind}-${row.userId}-${row.periodKey || ""}`,
      kind: String(row.kind || ""),
      emoji: row.emoji || "🏅",
      label: row.label || "Selo do dia",
      userId: Number(row.userId) || 0,
      userName: String(row.userName || "").trim(),
      userPhoto: row.userPhoto || null,
      score: Number(row.score) || 0,
      valueLabel: row.valueLabel || String(row.score || ""),
      copy: String(row.copy || "").trim(),
      fresh: Boolean(row.fresh),
      announcedAt: row.announcedAt || row.announced_at || null,
      periodKey: row.periodKey || "",
      reactions: normalizeHighlightReactions(row.reactions),
    }))
    .filter((row) => row.userId > 0 && row.kind);
}

export function highlightFirstName(name, fallback = "Colega") {
  const s = String(name || "").trim();
  if (!s) return fallback;
  return s.split(/\s+/)[0];
}

export function highlightSeenKey(item) {
  return `${item.periodKey || ""}:${item.kind}:${item.userId}`;
}

export function readSeenHighlights(storage) {
  try {
    const raw = storage?.getItem?.(VO_HIGHLIGHT_SEEN_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch {
    return new Set();
  }
}

export function markHighlightSeen(storage, item) {
  const key = highlightSeenKey(item);
  if (!key || key === "::") return false;
  const seen = readSeenHighlights(storage);
  if (seen.has(key)) return false;
  seen.add(key);
  try {
    storage?.setItem?.(VO_HIGHLIGHT_SEEN_KEY, JSON.stringify([...seen].slice(-80)));
  } catch {
    /* ignore */
  }
  return true;
}

export function honoreeSeal(items = [], userId) {
  const uid = Number(userId);
  if (!uid) return null;
  return (Array.isArray(items) ? items : []).find((row) => Number(row.userId) === uid) || null;
}

const SEAT_LABELS = {
  tasks_done: "Entregas",
  tasks_received: "Demandas",
  tasks_fast: "Ágil",
  service_messages: "Atendimento",
  service_fast: "Resposta",
  agenda_meetings: "Agenda",
};

/** Palavra curta no selo do avatar — o 📅 sozinho vira “dia 17” no macOS. */
export function highlightSeatLabel(item) {
  if (!item) return "";
  const kind = String(item.kind || "");
  if (SEAT_LABELS[kind]) return SEAT_LABELS[kind];
  const label = String(item.label || "").trim();
  if (label) return label.split(/\s+/)[0];
  return "Selo";
}

export function highlightSeatTitle(item) {
  if (!item) return "Selo do dia";
  const name = highlightFirstName(item.userName, "");
  const label = String(item.label || "Selo do dia").trim();
  const value = String(item.valueLabel || "").trim();
  const who = name ? ` de ${name}` : "";
  const score = value ? ` · ${value}` : "";
  return `${label}${who}${score}`;
}

export function highlightBarLabel(item) {
  const name = highlightFirstName(item?.userName);
  const seat = highlightSeatLabel(item);
  const value = String(item?.valueLabel || "").trim();
  return value ? `${name} · ${seat} · ${value}` : `${name} · ${seat}`;
}

export function speakCopy(fromName, toName) {
  return `${highlightFirstName(fromName, "Alguém")} foi falar com ${highlightFirstName(toName)}`;
}

export function cheerCopy(fromName, toName, emoji) {
  return `${highlightFirstName(fromName, "Alguém")} mandou ${emoji || "👏"} para ${highlightFirstName(toName)}`;
}
